import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  IconButton,
  TextField,
  Grid,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar,
  Badge,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import apiService from '../../services/apiService';
import warehouseTransferFix, {
  getAuthToken,
  getWarehouseTransfersWithRetry,
  directWarehouseTransfersAccess
} from '../../services/warehouseTransferFix';

// Types
interface Warehouse {
  _id: string;
  name: string;
}

interface TransferItem {
  product: string;
  productName: string;
  variantId: string | null;
  variantAttributes: Record<string, string>;
  quantity: number;
  unit: string;
  unitName: string;
  barcode?: string; // إضافة حقل الباركود
}

interface WarehouseTransfer {
  _id: string;
  sourceWarehouse: string;
  sourceWarehouseName: string;
  destinationWarehouse: string;
  destinationWarehouseName: string;
  status: 'draft' | 'completed';
  transferDate: string;
  notes?: string;
  items: TransferItem[];
  createdAt: string;
  completedAt?: string;
  referenceNumber?: string; // رقم المستند للربط مع الأحداث
}

// Interface for transfer event status
interface TransferEventStatus {
  status: 'success' | 'failed' | 'in_progress' | 'no_events' | 'unknown';
  hasErrors: boolean;
  totalEvents?: number;
  failedEvents?: number;
  successEvents?: number;
}

// Filter options
interface FilterOptions {
  sourceWarehouse: string;
  destinationWarehouse: string;
  status: string;
  startDate: string;
  endDate: string;
}

// Interface for transfer details dialog
interface TransferDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  transfer: WarehouseTransfer | null;
}

// Transfer Details Dialog Component
const TransferDetailsDialog: React.FC<TransferDetailsDialogProps> = ({ open, onClose, transfer }) => {
  if (!transfer) return null;
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="transfer-details-dialog-title"
    >
      <DialogTitle id="transfer-details-dialog-title">
        تفاصيل التحويل: {transfer._id}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">من المستودع:</Typography>
            <Typography variant="body1">{transfer.sourceWarehouseName}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">إلى المستودع:</Typography>
            <Typography variant="body1">{transfer.destinationWarehouseName}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">تاريخ التحويل:</Typography>
            <Typography variant="body1">
              {new Date(transfer.transferDate).toLocaleDateString('ar-SA')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">الحالة:</Typography>
            <Chip
              label={transfer.status === 'completed' ? 'مكتمل' : 'مسودة'}
              color={transfer.status === 'completed' ? 'success' : 'default'}
              size="small"
            />
          </Grid>
          {transfer.completedAt && (
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">تاريخ الإكمال:</Typography>
              <Typography variant="body1">
                {new Date(transfer.completedAt).toLocaleDateString('ar-SA')}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="subtitle2">ملاحظات:</Typography>
            <Typography variant="body1">{transfer.notes || 'لا توجد ملاحظات'}</Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              العناصر
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>المنتج</TableCell>
                    <TableCell>الباركود</TableCell>
                    <TableCell>الخصائص</TableCell>
                    <TableCell align="center">الكمية</TableCell>
                    <TableCell>الوحدة</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transfer.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.productName}</TableCell>
                      <TableCell>
                        {item.barcode ? (
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {item.barcode}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            لا يوجد
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.variantAttributes && Object.keys(item.variantAttributes).length > 0 ? (
                          <Box>
                            {Object.entries(item.variantAttributes).map(([key, value]) => (
                              <Chip
                                key={key}
                                label={`${key}: ${value}`}
                                size="small"
                                sx={{ m: 0.5 }}
                              />
                            ))}
                          </Box>
                        ) : (
                          'لا توجد خصائص'
                        )}
                      </TableCell>
                      <TableCell align="center">{item.quantity}</TableCell>
                      <TableCell>{item.unitName}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          إغلاق
        </Button>
        {transfer.status !== 'completed' && (
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              onClose();
              // نافذة تحرير التحويل
              window.location.href = `/warehouses/transfer/edit/${transfer._id}`;
            }}
          >
            تعديل
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// مكون الدائرة لحالة الأحداث
interface EventStatusIndicatorProps {
  status: TransferEventStatus | undefined;
  loading: boolean;
  referenceNumber: string;
  productCount?: number;
}

const EventStatusIndicator: React.FC<EventStatusIndicatorProps> = ({ status, loading, referenceNumber, productCount }) => {
  if (loading) {
    return (
      <Tooltip title="جاري التحقق من حالة الأحداث...">
        <CircularProgress size={20} />
      </Tooltip>
    );
  }

  if (!status || status.status === 'no_events' || status.status === 'unknown') {
    return (
      <Tooltip title="لا توجد أحداث مسجلة لهذا التحويل">
        <HelpOutlineIcon sx={{ color: 'grey.400', fontSize: 24 }} />
      </Tooltip>
    );
  }

  // حساب العدد المتوقع من الأحداث = (عدد المنتجات × 4) + 2
  const expectedEventCount = productCount ? (productCount * 4) + 2 : 0;
  const actualEventCount = status.totalEvents || 0;
  const eventsMatch = expectedEventCount > 0 && actualEventCount === expectedEventCount;

  if (status.status === 'success') {
    if (eventsMatch) {
      // الأحداث متطابقة - لون أخضر
      return (
        <Tooltip title={`جميع الأحداث نجحت (${status.successEvents || 0} حدث) - متطابقة مع العدد المتوقع`}>
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
        </Tooltip>
      );
    } else {
      // الأحداث غير متطابقة - لون أصفر
      return (
        <Tooltip title={`عدد الأحداث (${actualEventCount}) غير متطابقة مع العدد المتوقع (${expectedEventCount})`}>
          <ErrorIcon sx={{ color: 'warning.main', fontSize: 24 }} />
        </Tooltip>
      );
    }
  }

  if (status.status === 'failed' || status.hasErrors) {
    return (
      <Tooltip
        title={`يوجد أخطاء في الأحداث (${status.failedEvents || 0} فاشل من ${status.totalEvents || 0})`}
      >
        <ErrorIcon sx={{ color: 'error.main', fontSize: 24 }} />
      </Tooltip>
    );
  }

  if (status.status === 'in_progress') {
    return (
      <Tooltip title="التحويل قيد التنفيذ">
        <CircularProgress size={20} sx={{ color: 'warning.main' }} />
      </Tooltip>
    );
  }

  return null;
};

const WarehouseTransferList: React.FC = () => {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [transfers, setTransfers] = useState<WarehouseTransfer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [transferToDelete, setTransferToDelete] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTransfer, setSelectedTransfer] = useState<WarehouseTransfer | null>(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState<boolean>(false);
  
  // حالات الأحداث للتحويلات (للدوائر الخضراء/الحمراء)
  const [transferStatuses, setTransferStatuses] = useState<Record<string, TransferEventStatus>>({});
  const [loadingStatuses, setLoadingStatuses] = useState<boolean>(false);
  
  // Pagination
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  
  // Filtering
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    sourceWarehouse: '',
    destinationWarehouse: '',
    status: '',
    startDate: '',
    endDate: ''
  });

  // Retry mechanism
  const [retryCount, setRetryCount] = useState<number>(0);
  const [retryDelay, setRetryDelay] = useState<number>(2000); // 2 seconds initial retry delay
  const maxRetries = 8; // زيادة عدد محاولات إعادة الاتصال لمدة أطول

  // Alert
  const [alert, setAlert] = useState<{type: 'success' | 'error' | 'info', message: string} | null>(null);

  // Load warehouses on component mount and inject authentication token
  useEffect(() => {
    // Define interface for warehouse API response
    interface WarehouseApiResponse {
      success: boolean;
      warehouses: Warehouse[];
    }

    const fetchWarehouses = async () => {
      try {
        // استخدام توكن المصادقة الحالي
        getAuthToken();
        
        // Try first with regular apiService
        try {
          const response = await apiService.get<WarehouseApiResponse>('warehouse-transfers/warehouses');
          if (response.success) {
            setWarehouses(response.warehouses);
            return;
          }
        } catch (error) {
          console.warn('Initial warehouse fetch failed, trying fallback...', error);
        }
        
        // Fallback: try direct API access without the /api prefix (common error)
        try {
          const response = await axios.get<WarehouseApiResponse>(`${API_BASE_URL}/warehouse-transfers/warehouses`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (response.data && response.data.success) {
            setWarehouses(response.data.warehouses);
          }
        } catch (secondError) {
          console.error('Warehouse fetch fallback also failed:', secondError);
        }
      } catch (error) {
        console.error('Error loading warehouses:', error);
      }
    };

    fetchWarehouses();
  }, []);

  // جلب حالات الأحداث للتحويلات المعروضة
  useEffect(() => {
    const fetchTransferStatuses = async () => {
      if (transfers.length === 0) return;
      
      setLoadingStatuses(true);
      try {
        // جمع أرقام المستندات من التحويلات
        const referenceNumbers = transfers
          .map(t => t.referenceNumber || t._id)
          .filter(Boolean);
        
        if (referenceNumbers.length === 0) {
          setLoadingStatuses(false);
          return;
        }

        // طلب حالات جميع التحويلات دفعة واحدة
        const response = await axios.post(
          `${API_BASE_URL}/inventory-events/transfer-status/bulk`,
          { referenceNumbers },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        if (response.data.success) {
          setTransferStatuses(response.data.statuses);
        }
      } catch (error) {
        console.error('خطأ في جلب حالات التحويلات:', error);
        // لا نعرض خطأ للمستخدم لأن هذه ميزة إضافية
      } finally {
        setLoadingStatuses(false);
      }
    };

    // تأخير بسيط لتجنب الطلبات المتزامنة
    const timer = setTimeout(() => {
      fetchTransferStatuses();
    }, 500);

    return () => clearTimeout(timer);
  }, [transfers]);

  // Load transfers with filtering and pagination - enhanced with warehouseTransferFix utilities
  useEffect(() => {
    const fetchTransfers = async (attempt = 0) => {
      if (attempt === 0) {
        setLoading(true);
        setError(null); // Reset error state at the start of each request
      }
      
      // تأخير أولي أطول قبل المحاولة الأولى للسماح للخادم بالتهيئة الكاملة
      if (attempt === 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      try {
        // تأكد من وجود توكن صالح
        getAuthToken();
        
        // Build query parameters
        const params = new URLSearchParams();
        if (filterOptions.sourceWarehouse) {
          params.append('sourceWarehouse', filterOptions.sourceWarehouse);
        }
        if (filterOptions.destinationWarehouse) {
          params.append('destinationWarehouse', filterOptions.destinationWarehouse);
        }
        if (filterOptions.status) {
          params.append('status', filterOptions.status);
        }
        if (filterOptions.startDate) {
          params.append('startDate', filterOptions.startDate);
        }
        if (filterOptions.endDate) {
          params.append('endDate', filterOptions.endDate);
        }
        // Add search term if provided
        if (searchTerm.trim()) {
          params.append('search', searchTerm.trim());
        }
        // Add pagination
        params.append('page', (page + 1).toString());
        params.append('limit', rowsPerPage.toString());

        console.log(`Fetching transfers (attempt ${attempt + 1}/${maxRetries + 1}) with params:`, params.toString());
        
        // First try with the specialized warehouse transfers function
        try {
          const response = await getWarehouseTransfersWithRetry(3, 2000, params.toString());
          
          if (response.success) {
            setTransfers(response.transfers || []);
            setTotalCount(response.total || 0);
            setRetryCount(0);
            setRetryDelay(2000);
            setLoading(false);
            return;
          }
        } catch (specializedError) {
          console.warn('Enhanced transfer fetch method failed, falling back to standard method:', specializedError);
        }
        
        // If the specialized method fails, try the standard method
        const response = await apiService.get<{
          success: boolean;
          transfers: WarehouseTransfer[];
          total: number;
          message?: string;
        }>(`warehouse-transfers?${params.toString()}`, {
          timeout: 20000 // 20 seconds timeout
        });
        
        if (response.success) {
          setTransfers(response.transfers);
          setTotalCount(response.total || 0);
          // Reset retry counters on success
          setRetryCount(0);
          setRetryDelay(2000);
          // تحديث حالة التحميل إلى false عند نجاح الاستجابة
          setLoading(false);
        } else {
          setError(response.message || 'حدث خطأ غير معروف');
          setTransfers([]);
          setTotalCount(0);
          setLoading(false);
        }
      } catch (error: any) {
        console.error(`Error loading transfers (attempt ${attempt + 1}):`, error);
        
        // Before giving up, try direct access method as a last resort
        if (attempt === maxRetries - 1) {
          try {
            console.log('Trying direct warehouse transfers access as a final attempt...');
            const directResult = await directWarehouseTransfersAccess(rowsPerPage);
            
            if (directResult.success) {
              setTransfers(directResult.transfers);
              setTotalCount(directResult.total || 0);
              setRetryCount(0);
              setRetryDelay(2000);
              setLoading(false);
              return;
            }
          } catch (directError) {
            console.error('Direct access method also failed:', directError);
          }
        }
        
        // تحسين معالجة رسائل الخطأ
        let errorMessage = 'حدث خطأ أثناء تحميل عمليات التحويل';
        let canRetry = true;
        
        if (error.response) {
          // Server responded with error
          const serverError = error.response.data;
          const statusCode = error.response.status;
          
          // معالجة خطأ عدم المصادقة بشكل خاص
          if (statusCode === 401) {
            // استخدام توكن المصادقة الحالي
            getAuthToken();
            errorMessage = 'تم تخطي تسجيل الدخول للوصول إلى سجل التحويلات';
            canRetry = true; // Try once more after injecting token
            
            // لا نقوم بإعادة التوجيه إلى صفحة تسجيل الدخول
            setAlert({
              type: 'info',
              message: 'استمرار العرض بدون تسجيل دخول. بعض العمليات قد تتطلب تسجيل الدخول.'
            });
          }
          // تجنب عرض رسائل الخطأ التقنية أو الغامضة للمستخدم
          else if (serverError.message && !serverError.message.includes('تم إزالة وظيفة')) {
            errorMessage = serverError.message;
          } else {
            // رسالة خطأ أكثر وضوحًا في حالة رسائل الخطأ الغامضة
            errorMessage = 'فشل الاتصال بخدمة تحويلات المستودعات';
          }
          
          console.error('Server error details:', serverError, 'Status code:', statusCode);
          
          // Don't retry on 4xx errors (client errors), unless it's an auth error on first attempt
          if (statusCode >= 400 && statusCode < 500 && !(statusCode === 401 && attempt <= 1)) {
            canRetry = false;
          }
        } else if (error.request) {
          // No response received
          errorMessage = 'لم يتم تلقي استجابة من الخادم. يرجى التحقق من اتصالك بالإنترنت وأن الخادم يعمل بشكل صحيح';
          console.error('No response received:', error.request);
        } else {
          // Request setup error
          errorMessage = `خطأ في إعداد الطلب: ${error.message}`;
        }
        
        // Check if we should attempt retry
        if (canRetry && attempt < maxRetries) {
          setRetryCount(attempt + 1);
          // Set progressively longer delays between retries (exponential backoff)
          const newDelay = Math.min(retryDelay * 1.5, 30000); // Cap at 30 seconds
          setRetryDelay(newDelay);
          
          setError(`${errorMessage} - جاري محاولة الاتصال (${attempt + 1}/${maxRetries})...`);
          
          // Wait for delay and then retry
          setTimeout(() => {
            fetchTransfers(attempt + 1);
          }, newDelay);
          return; // Exit early to avoid setting loading to false
        }
        
        // إذا فشلت جميع المحاولات، اعرض رسالة خطأ أكثر تفصيلاً وإرشادات للحل
        setError(`فشل الاتصال بخدمة تحويلات المستودعات - قد تكون هناك مشكلة في اتصال قاعدة البيانات`);
        setTransfers([]);
        setTotalCount(0);
        // تحديث حالة التحميل إلى false عند فشل الاتصال نهائيًا
        setLoading(false);
        
        // إضافة زر "إعادة المحاولة" بعد فترة قصيرة لتحسين تجربة المستخدم
        setTimeout(() => {
          setAlert({
            type: 'info',
            message: 'يمكنك إعادة تشغيل الخوادم من خلال النقر على زر "إعادة تشغيل الخوادم" أسفل الصفحة'
          });
        }, 3000);
      }
    };

    // Debounce the fetch operation to avoid too many requests while typing in search box
    const timeoutId = setTimeout(() => {
      fetchTransfers(0);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, filterOptions, searchTerm, API_BASE_URL, maxRetries, retryDelay]);

  // Handle page change
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle filter changes
  const handleFilterChange = (name: keyof FilterOptions, value: string) => {
    setFilterOptions(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(0); // Reset to first page when filter changes
  };

  // Reset filters
  const resetFilters = () => {
    setFilterOptions({
      sourceWarehouse: '',
      destinationWarehouse: '',
      status: '',
      startDate: '',
      endDate: ''
    });
    setSearchTerm('');
    setPage(0);
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Navigate to create new transfer
  const goToNewTransfer = () => {
    navigate('/warehouses/transfer');
  };

  // View transfer details
  const viewTransferDetails = (transferId: string) => {
    const fetchTransferDetails = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/warehouse-transfers/${transferId}`);
        if (response.data.success) {
          // Almacenar el transfer seleccionado y abrir el diálogo
          setSelectedTransfer(response.data.transfer);
          setIsDetailsDialogOpen(true);
          
          // Mostrar mensaje de éxito
          setAlert({
            type: 'success',
            message: `تم تحميل تفاصيل التحويل: ${response.data.transfer._id}`
          });
        }
      } catch (error) {
        setAlert({
          type: 'error',
          message: 'فشل في تحميل تفاصيل التحويل'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTransferDetails();
  };

  // Edit transfer
  const handleEditTransfer = (transferId: string) => {
    navigate(`/warehouses/transfer/edit/${transferId}`);
  };

  // Delete transfer
  const handleDeleteTransfer = (transferId: string) => {
    setTransferToDelete(transferId);
    setIsDeleteDialogOpen(true);
  };

  // Confirm delete transfer
  const confirmDeleteTransfer = async () => {
    if (!transferToDelete) return;
    
    setLoading(true);
    try {
      // استخدام الـ API لحذف التحويل
      const response = await axios.delete(`${API_BASE_URL}/warehouse-transfers/${transferToDelete}`);
      
      if (response.data.success) {
        setAlert({
          type: 'success',
          message: 'تم حذف التحويل بنجاح'
        });
        
        // تحديث القائمة بعد الحذف
        setTransfers(transfers.filter(transfer => transfer._id !== transferToDelete));
      } else {
        setAlert({
          type: 'error',
          message: response.data.message || 'حدث خطأ أثناء حذف التحويل'
        });
      }
    } catch (error: any) {
      console.error('Error deleting transfer:', error);
      setAlert({
        type: 'error',
        message: error.response?.data?.message || 'فشل في حذف التحويل'
      });
    } finally {
      setLoading(false);
      setIsDeleteDialogOpen(false);
      setTransferToDelete(null);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setTransferToDelete(null);
  };

  // Close alert
  const handleCloseAlert = () => {
    setAlert(null);
  };

  // Get warehouse name by ID
  const getWarehouseName = (id: string) => {
    const warehouse = warehouses.find(w => w._id === id);
    return warehouse ? warehouse.name : 'غير معروف';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">سجل التحويلات بين المستودعات</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircleOutlineIcon />}
          onClick={goToNewTransfer}
        >
          تحويل جديد
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="بحث"
              variant="outlined"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              placeholder="بحث بالاسم أو الرقم"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              onClick={() => setFilterOpen(!filterOpen)}
              startIcon={<FilterListIcon />}
              sx={{ height: '40px' }}
              fullWidth
            >
              {filterOpen ? 'إخفاء الفلاتر' : 'عرض الفلاتر'}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              onClick={resetFilters}
              sx={{ height: '40px' }}
              disabled={!Object.values(filterOptions).some(value => value !== '')}
              fullWidth
            >
              إعادة ضبط الفلاتر
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              sx={{ height: '40px' }}
              fullWidth
            >
              تصدير
            </Button>
          </Grid>

          {filterOpen && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="من المستودع"
                  value={filterOptions.sourceWarehouse}
                  onChange={(e) => handleFilterChange('sourceWarehouse', e.target.value)}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">
                    <em>الكل</em>
                  </MenuItem>
                  {warehouses.map((warehouse) => (
                    <MenuItem key={warehouse._id} value={warehouse._id}>
                      {warehouse.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="إلى المستودع"
                  value={filterOptions.destinationWarehouse}
                  onChange={(e) => handleFilterChange('destinationWarehouse', e.target.value)}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">
                    <em>الكل</em>
                  </MenuItem>
                  {warehouses.map((warehouse) => (
                    <MenuItem key={warehouse._id} value={warehouse._id}>
                      {warehouse.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  label="الحالة"
                  value={filterOptions.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">
                    <em>الكل</em>
                  </MenuItem>
                  <MenuItem value="draft">مسودة</MenuItem>
                  <MenuItem value="completed">مكتمل</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="من تاريخ"
                  type="date"
                  value={filterOptions.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="إلى تاريخ"
                  type="date"
                  value={filterOptions.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Transfers Table */}
      <Paper>
        <TableContainer>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ m: 2 }}>
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setError(null);
                      setLoading(true);
                      // Reset retry counters
                      setRetryCount(0);
                      setRetryDelay(2000);
                      // Force re-fetch by changing a dependency in the useEffect
                      setPage(page);
                    }}
                  >
                    إعادة المحاولة
                  </Button>
                }
              >
                {error}
              </Alert>
              
              {retryCount > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  محاولة {retryCount}/{maxRetries}... الرجاء الانتظار
                </Alert>
              )}
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                تأكد من وجود اتصال بالإنترنت وأن الخادم يعمل بشكل صحيح.
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <Alert severity="info" sx={{ mb: 0 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    هل تواجه مشكلة في الاتصال؟ جرب الحلول التالية:
                  </Typography>
                  <ul style={{ paddingLeft: '20px', margin: '5px 0', textAlign: 'right' }}>
                    <li>تحقق من اتصالك بالإنترنت</li>
                    <li>تأكد أن الخادم في حالة تشغيل</li>
                    <li>حاول إعادة تشغيل الخوادم</li>
                  </ul>
                </Alert>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => {
                      setError(null);
                      setLoading(true);
                      // Reset retry counters
                      setRetryCount(0);
                      setRetryDelay(2000);
                      // Force re-fetch by changing a dependency in the useEffect
                      setPage(page);
                    }}
                  >
                    تحديث الصفحة
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                      setLoading(true);
                      setError('جاري فحص حالة الخدمات... برجاء الانتظار');
                      
                      try {
                        // فحص حالة الخدمات
                        const healthResponse = await axios.get(`${API_BASE_URL}/health`, { timeout: 5000 });
                        
                        if (healthResponse.data && healthResponse.data.status === 'ok') {
                          // فحص خدمة تحويلات المستودعات
                          try {
                            await axios.get(`${API_BASE_URL}/warehouse-transfers/warehouses`, { timeout: 5000 });
                            setAlert({
                              type: 'success',
                              message: 'جميع الخدمات تعمل بشكل صحيح! حاول تحديث الصفحة مرة أخرى.'
                            });
                          } catch (transfersError) {
                            setAlert({
                              type: 'error',
                              message: 'الخادم يعمل لكن خدمة تحويلات المستودعات لا تستجيب. قد تكون هناك مشكلة في الاتصال بقاعدة البيانات.'
                            });
                          }
                        } else {
                          setAlert({
                            type: 'error',
                            message: 'خادم API غير متاح أو يواجه مشكلة في الاتصال بقاعدة البيانات.'
                          });
                        }
                      } catch (error) {
                        setAlert({
                          type: 'error',
                          message: 'تعذر الاتصال بالخادم. تأكد من تشغيل الخوادم واتصال قاعدة البيانات.'
                        });
                      } finally {
                        setLoading(false);
                        setError('فشل الاتصال بخدمة تحويلات المستودعات - قد تكون هناك مشكلة في اتصال قاعدة البيانات');
                      }
                    }}
                  >
                    فحص حالة الخدمات
                  </Button>
                  
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={() => {
                      // فتح نافذة تأكيد لإعادة تشغيل الخوادم
                      if (window.confirm('هل تريد إعادة تشغيل الخوادم؟ سيتم إغلاق الصفحة الحالية وفتح الخوادم من جديد.')) {
                        // استخدام التصفح إلى صفحة restart.html التي ستقوم بتنفيذ سكريبت إعادة التشغيل
                        window.location.href = '/restart.html';
                      }
                    }}
                  >
                    إعادة تشغيل الخوادم
                  </Button>
                </Box>
              </Box>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">حالة الأحداث</TableCell>
                  <TableCell>الرقم</TableCell>
                  <TableCell>تاريخ التحويل</TableCell>
                  <TableCell>من المستودع</TableCell>
                  <TableCell>إلى المستودع</TableCell>
                  <TableCell>عدد العناصر</TableCell>
                  <TableCell>الحالة</TableCell>
                  <TableCell>تاريخ الإنشاء</TableCell>
                  <TableCell>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transfers.length > 0 ? (
                  transfers.map((transfer) => {
                    const referenceNumber = transfer.referenceNumber || transfer._id;
                    const eventStatus = transferStatuses[referenceNumber];
                    
                    return (
                      <TableRow key={transfer._id}>
                        <TableCell align="center">
                           <EventStatusIndicator
                             status={eventStatus}
                             loading={loadingStatuses}
                             referenceNumber={referenceNumber}
                             productCount={transfer.items.length}
                           />
                         </TableCell>
                        <TableCell>{transfer._id.substring(transfer._id.length - 6)}</TableCell>
                        <TableCell>{formatDate(transfer.transferDate)}</TableCell>
                        <TableCell>{transfer.sourceWarehouseName}</TableCell>
                        <TableCell>{transfer.destinationWarehouseName}</TableCell>
                        <TableCell>
                          <Badge badgeContent={transfer.items.length} color="primary">
                            <Box sx={{ minWidth: 20 }} />
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={transfer.status === 'completed' ? 'مكتمل' : 'مسودة'}
                            color={transfer.status === 'completed' ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(transfer.createdAt)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              color="primary"
                              onClick={() => viewTransferDetails(transfer._id)}
                              size="small"
                              title="عرض التفاصيل"
                            >
                              <VisibilityIcon />
                            </IconButton>
                            <IconButton
                              color="info"
                              onClick={() => handleEditTransfer(transfer._id)}
                              size="small"
                              title="تعديل"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteTransfer(transfer._id)}
                              size="small"
                              title="حذف"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      لا توجد عمليات تحويل مطابقة للبحث
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="عدد الصفوف:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} من ${count !== -1 ? count : `أكثر من ${to}`}`
          }
        />
      </Paper>

      {/* Alert Snackbar */}
      <Snackbar
        open={!!alert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert?.type || 'info'} sx={{ width: '100%' }}>
          {alert?.message || ''}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          تأكيد الحذف
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            هل أنت متأكد من رغبتك في حذف هذا التحويل؟ لا يمكن التراجع عن هذا الإجراء.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="primary">
            إلغاء
          </Button>
          <Button onClick={confirmDeleteTransfer} color="error" autoFocus>
            حذف
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Transfer Details Dialog */}
      <TransferDetailsDialog
        open={isDetailsDialogOpen}
        onClose={() => setIsDetailsDialogOpen(false)}
        transfer={selectedTransfer}
      />
    </Box>
  );
};

export default WarehouseTransferList;